<template>
  <div class="race-page">
    <div class="race-selection-container">
      <h1>Welcome to Celestia</h1>
      <p class="intro-text">
        Your journey begins now. Choose your race to shape your destiny.
      </p>

      <div class="race-selection-layout">
        <div v-if="races.length === 0">
          <p>Loading races...</p>
        </div>
        <div v-else class="races-grid">
          <Card
            v-for="race in races"
            :key="race.slug"
            :title="race.name"
            :description="race.kingdom"
            :image="getRaceImage(race.name)"
            :class="{
              selected: selectedRace && selectedRace.slug === race.slug,
            }"
            @card-click="selectRaceCard(race)"
          />
        </div>

        <div v-if="selectedRace" class="details-column">
          <div class="race-details">
            <h3>{{ selectedRace.name }}</h3>
            <p class="description">{{ selectedRace.description }}</p>
            <h4>Bonuses:</h4>
            <p>{{ selectedRace.bonus }}</p>
            <h4>Starting Units:</h4>
            <div v-if="startingUnitList.length" class="starting-units">
              <div
                v-for="unit in startingUnitList"
                :key="unit.slug || unit.name"
                class="starting-unit"
              >
                <p class="starting-unit-title">
                  <i
                    v-if="unit.icon"
                    :class="unit.icon"
                    :style="{ color: unit.color }"
                  ></i>
                  <span>{{ unit.role }} — {{ unit.name }}</span>
                </p>
                <p class="starting-unit-stats">
                  HP {{ unit.hp }} · ATK {{ unit.atk }} · DEF {{ unit.def }} · SPD
                  {{ unit.spd }}
                </p>
                <p class="starting-unit-desc">{{ unit.description }}</p>
              </div>
            </div>
            <p v-else>{{ selectedRace.starting_units }}</p>
          </div>
          <div class="race-confirm-bar">
            <p v-if="confirmError" class="race-confirm-error">{{ confirmError }}</p>
            <button @click="confirmSelection" :disabled="isConfirming">
              {{
                isConfirming
                  ? "Confirming..."
                  : "Choose the " + selectedRace.name
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, computed, ref, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../store/auth";
import { playSound } from "../utils/audio";
import Card from "../components/Card.vue";
import { unitDisplayMap } from "../utils/unitDisplay";
import unitsRoster from "@/data/units.json";

function getRaceImages() {
  const context = require.context("../assets/images/races", false, /\.jpg$/);
  const images = {};
  context.keys().forEach((key) => {
    const raceName = key.replace("./", "").replace(".jpg", "");
    images[raceName] = context(key);
  });
  return images;
}

const raceImages = getRaceImages();
const authStore = useAuthStore();
const router = useRouter();

const races = computed(() => authStore.races);
const selectedRace = ref(null);
const isConfirming = ref(false);
const confirmError = ref("");

const STARTING_UNIT_SLUGS = ["defender", "scout", "melee"];

const startingUnitList = computed(() => {
  const race = selectedRace.value;
  if (!race) return [];

  if (Array.isArray(race.starting_units) && race.starting_units.length) {
    return race.starting_units.map((unit) => {
      const slug = String(unit.slug || "").toLowerCase();
      const display = unitDisplayMap[slug] || {};
      return {
        ...unit,
        icon: display.icon || "",
        color: display.color || "#d8d0c0",
      };
    });
  }

  const roster = unitsRoster[race.slug] || {};
  return STARTING_UNIT_SLUGS.map((slug) => {
    const unit = roster[slug];
    const display = unitDisplayMap[slug] || {};
    if (!unit) return null;
    return {
      slug,
      role: display.name || slug,
      name: unit.name,
      hp: unit.hp,
      atk: unit.atk,
      def: unit.def,
      spd: unit.spd,
      rng: unit.rng,
      description: unit.description,
      icon: display.icon || "",
      color: display.color || "#d8d0c0",
    };
  }).filter(Boolean);
});

let previousScrollRestoration = null;

const scrollToTop = () => {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

onMounted(() => {
  if ("scrollRestoration" in history) {
    previousScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = "manual";
  }
  scrollToTop();

  authStore.fetchRaces();
  if (races.value.length > 0) {
    selectedRace.value = races.value[0];
    nextTick(scrollToTop);
  } else {
    const unwatch = watch(races, (newRaces) => {
      if (newRaces.length > 0) {
        selectedRace.value = newRaces[0];
        unwatch();
        nextTick(scrollToTop);
      }
    });
  }
});

onUnmounted(() => {
  if (previousScrollRestoration != null && "scrollRestoration" in history) {
    history.scrollRestoration = previousScrollRestoration;
  }
});

const getRaceImage = (raceName) => {
  return raceImages[raceName] || "";
};

const selectRaceCard = (race) => {
  playSound("futuristic-click.mp3");
  selectedRace.value = race;
};

const confirmSelection = async () => {
  if (!selectedRace.value) return;
  playSound("futuristic-click.mp3");
  confirmError.value = "";
  isConfirming.value = true;
  try {
    await authStore.selectRace(selectedRace.value.slug);
    await router.push("/game/home");
  } catch (error) {
    const status = error.response?.status || null;
    confirmError.value =
      status === 401
        ? "Your login session was not saved in this browser, so race selection cannot continue. Log in again, or disable Prevent Cross-Site Tracking for this site."
        : error.response?.data?.message ||
          error.response?.data?.error ||
          (error.code === "ECONNABORTED"
            ? "Request timed out. Try again."
            : "Could not save your race. Try again.");
    console.error("Failed to confirm race selection:", error);
  } finally {
    isConfirming.value = false;
  }
};
</script>

<style scoped>
.race-page {
  width: 100%;
  box-sizing: border-box;
}

.race-selection-container {
  position: relative;
  isolation: isolate;
  max-width: 1200px;
  margin: 2rem auto;
  padding: 2.5rem;
  background-color: transparent;
  border: 1px solid rgba(0, 246, 255, 0.5);
  border-radius: 10px;
  box-shadow: 0 0 25px rgba(0, 246, 255, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  box-sizing: border-box;
  height: auto;
  min-height: auto;
  max-height: none;
}

.race-selection-container::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  z-index: -1;
  background-color: rgba(10, 10, 20, 0.75);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.race-selection-container h1 {
  text-align: center;
  width: 100%;
}

.intro-text {
  max-width: 600px;
  color: #c0c0c0;
  text-align: center;
}

.race-selection-layout {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
}

.races-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  width: 100%;
  flex: 1;
}

.races-grid .selected {
  background-color: rgba(0, 246, 255, 0.3);
  box-shadow: 0 0 15px rgba(0, 246, 255, 0.7);
  transform: translateY(-5px);
}

.details-column {
  width: 100%;
  max-width: 700px;
}

.race-details {
  margin-top: 0;
  padding: 1.5rem;
  border: 1px solid #00f6ff;
  border-radius: 8px;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  flex: 2;
  max-width: 700px;
  overflow-anchor: none;
  box-sizing: border-box;
}

.race-details h3 {
  color: #ff00ff;
  margin-top: 0;
}
.race-details ul {
  list-style: none;
  padding: 0;
  text-align: left;
}
.race-details li {
  margin-bottom: 0.5rem;
}

.starting-units {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
  width: 100%;
}

.starting-unit-title,
.starting-unit-stats,
.starting-unit-desc {
  margin: 0.15rem 0;
}

.starting-unit-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: bold;
  color: #ffffff;
}

.starting-unit-title i {
  width: 1.1rem;
  text-align: center;
}

.starting-unit-stats {
  font-size: 0.8rem;
  color: #00f6ff;
  letter-spacing: 0.02em;
}

.starting-unit-desc {
  font-size: 0.8rem;
  color: #c0c0c0;
}

.race-confirm-bar {
  width: 100%;
  max-width: 700px;
  margin: 0 auto 1.5rem;
}

.race-confirm-bar button {
  width: 100%;
}

.race-confirm-error {
  color: #ff4d4d;
  font-weight: bold;
  text-align: center;
  margin: 0 0 0.75rem;
}

@media (min-width: 900px) {
  .race-page {
    min-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    box-sizing: border-box;
  }

  .race-selection-container {
    width: 100%;
    height: auto;
    max-height: none;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: stretch;
    box-sizing: border-box;
    padding: 1.5rem 2rem;
    margin: 0 auto;
    min-height: 0;
  }

  .race-selection-container h1 {
    font-size: 2rem;
    margin: 0 0 0.25rem 0;
  }

  .intro-text {
    margin: 0 auto 1rem;
    font-size: 0.95rem;
  }

  .race-selection-layout {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    width: 100%;
  }

  .races-grid {
    grid-template-columns: repeat(5, minmax(140px, 1fr));
    gap: 0.75rem;
    overflow-x: auto;
    overflow-y: hidden;
    flex-shrink: 0;
    min-height: 0;
    max-width: none;
    padding-top: 6px;
  }

  .races-grid :deep(.card-image-container) {
    height: 190px;
  }

  .races-grid :deep(.card-content) {
    padding: 0.4rem 0.5rem;
  }

  .races-grid :deep(.card-title) {
    font-size: 0.95rem;
    margin: 0 0 0.15rem 0;
  }

  .races-grid :deep(.card-description) {
    font-size: 0.7rem;
  }

  .details-column {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: none;
    height: auto;
    flex: 1;
    min-height: 0;
  }

  .race-details {
    flex: 0 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 1.25rem;
    max-width: 100%;
    overflow-y: auto;
  }

  .race-details h3,
  .race-details h4,
  .race-details p {
    margin: 0.25rem 0;
  }

  .race-details h3 {
    margin-top: 0;
  }

  .starting-units {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .starting-unit-title {
    font-size: 0.85rem;
  }

  .starting-unit-stats,
  .starting-unit-desc {
    font-size: 0.72rem;
  }

  .race-confirm-bar {
    margin-top: 0.75rem;
    margin-bottom: 0;
    max-width: none;
    flex-shrink: 0;
  }
}

@media (max-width: 899px) {
  .race-page {
    padding-bottom: calc(90px + env(safe-area-inset-bottom, 0px));
  }

  .race-selection-container {
    margin: 0.5rem auto;
    padding: 1rem 1rem 1.25rem;
    gap: 0.85rem;
    height: auto;
    min-height: auto;
    max-height: none;
    overflow: visible;
    align-items: stretch;
  }

  .races-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .races-grid :deep(.card-image-container) {
    height: 96px;
  }

  .race-details {
    padding: 1rem;
    margin-bottom: 0;
  }

  .race-details .description {
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .starting-units {
    gap: 0.5rem;
  }

  .starting-unit-title {
    font-size: 0.85rem;
  }

  .starting-unit-stats,
  .starting-unit-desc {
    font-size: 0.72rem;
  }

  .race-confirm-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 30;
    max-width: none;
    margin: 0;
    padding: 0.75rem 1rem
      calc(0.75rem + env(safe-area-inset-bottom, 0px));
    background: rgba(10, 10, 20, 0.94);
    border-top: 1px solid rgba(0, 246, 255, 0.45);
    box-sizing: border-box;
  }
}

@media (max-width: 768px) {
  .race-selection-container {
    margin: 0.5rem auto;
    padding: 1rem 1rem 1.25rem;
    gap: 0.65rem;
  }

  .race-selection-container h1 {
    font-size: 1.4rem;
    margin: 0;
  }

  .intro-text {
    font-size: 0.85rem;
    margin: 0;
  }

  .race-selection-layout {
    gap: 0.75rem;
  }

  .races-grid {
    gap: 0.5rem;
  }

  .races-grid :deep(.card-image-container) {
    height: 88px;
  }

  .races-grid :deep(.card-content) {
    padding: 0.5rem 0.4rem;
  }

  .races-grid :deep(.card-title) {
    font-size: 0.95rem;
    margin: 0 0 0.25rem 0;
  }

  .races-grid :deep(.card-description) {
    font-size: 0.75rem;
  }

  .races-grid .selected {
    transform: none;
  }

  .race-details {
    margin-bottom: 0;
  }
}
</style>
